import type { Cache } from '@wix/thunderbolt-ssr-api'
import type {
	AppSpecData,
	BuilderCompData,
	BuilderComponentsData,
	BuilderCompTranslations,
	BuilderCtxProviderData,
	BuilderContextProvidersData,
	BuilderPublicPackagesUrls,
	Experiments,
	ViewerClientSpecMap,
	BuilderFunctionLibrariesData,
	BuilderFuncLibData,
	ComponentExposedContextsData,
	ViewerBuilderCompData,
} from '@wix/thunderbolt-symbols'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'
import { NoopCache } from '../withCache'

export const VIEWER_ASSETS_BASE_URL = 'https://viewer-assets.cdn.inventica.ru'
const LOCAL_PILER_STATICS = 'http://localhost:3003'

export const isPilerComponent = (componentUrl: string) => componentUrl.includes('piler-router')

// Builder design: a component resolves both by its manifest moduleSpecifier and by its public npm name, package.name/component.path.
export const getBuilderComponentModuleSpecifiers = (
	moduleSpecifier: string | null | undefined,
	moduleConfiguration: BuilderCompData['moduleConfiguration']
): Array<string> => {
	const packageName = moduleConfiguration?.package?.name
	const componentPath = moduleConfiguration?.component?.path
	const specifiers = [moduleSpecifier, packageName && componentPath && `${packageName}/${componentPath}`]
	return [...new Set(specifiers.filter((specifier): specifier is string => !!specifier))]
}

// Specs propagated to VAG (also through BCC, which calls VAG internally) as the `experiments` query param.
// See docs/propagating-experiments-to-vag-bcc.md
export const MANIFEST_FETCH_EXPERIMENTS: ReadonlyArray<string> = []

export const buildManifestFetchUrl = (
	url: string,
	experiments: Experiments,
	propagatedExperiments: ReadonlyArray<string>
): string => {
	const enabled = propagatedExperiments.filter((spec) => isExperimentOpen(experiments, spec))
	if (!enabled.length) {
		return url
	}
	const result = new URL(url)
	const existing = result.searchParams.get('experiments')
	const existingSpecs = existing ? existing.split(',').filter(Boolean) : []
	const merged = Array.from(new Set([...existingSpecs, ...enabled]))
	result.searchParams.set('experiments', merged.join(','))
	return result.toString()
}

export type IGetBuilderComponentsData = (
	clientSpecMap: ViewerClientSpecMap,
	isBuilderComponentModel?: boolean,
	serviceTopology?: Record<string, string>,
	builderPublicManifest?: Record<string, string>,
	useLocalPiler?: boolean,
	debug?: boolean
) => BuilderComponentsData

export type IGetBuilderContextProvidersData = (
	clientSpecMap: ViewerClientSpecMap,
	isBuilderComponentModel?: boolean
) => BuilderContextProvidersData

export type IGetBuilderFunctionLibrariesData = (
	clientSpecMap: ViewerClientSpecMap,
	isBuilderComponentModel?: boolean
) => BuilderFunctionLibrariesData

export type IGetComponentExposedContextsData = (
	clientSpecMap: ViewerClientSpecMap,
	isBuilderComponentModel?: boolean
) => ComponentExposedContextsData

export type IGetBuilderComponentsTranslations = (
	clientSpecMap: ViewerClientSpecMap
) => Record<string, BuilderCompTranslations>

const builderPublicPackages = [
	'builder-interactions',
	'builder-state',
	'builder-state-react',
	'builder-velo',
	'builder-velo-react',
	'builder-element-removal',
	'builder-utils',
]

export const PILER_SITEASSETS_PACKAGE = '@wix/piler-siteassets'

export const getBuilderPublicManifest = async (
	baseUrl: string,
	version: string,
	cache: Cache<string, string> = NoopCache()
): Promise<Record<string, string>> => {
	const url = `${baseUrl}/builder-public-manifests/${version}/piler-siteassets-manifest.json`

	if (cache.get(url)) {
		return JSON.parse(cache.get(url)!)
	}

	return fetch(url)
		.then((res) => {
			if (!res.ok) {
				throw new Error(`failed to fetch builder public manifest from ${url}: ${res.statusText}`)
			}
			return res.text()
		})
		.then((txt) => {
			cache.set(url, txt)
			return JSON.parse(txt)
		})
}

export const getPublicPackagesUrls = (
	serviceTopology: Record<string, string>,
	builderPublicManifest: Record<string, string> = {},
	useLocalPiler = false,
	debug = false
): BuilderPublicPackagesUrls => {
	const getUrlFor = (pkg: string) => {
		const base = serviceTopology[pkg]
		const url = builderPublicManifest[pkg] ? `${base.replace(/\/[^/]+$/, '')}/${builderPublicManifest[pkg]}` : base

		if (pkg === 'services-manager-react' || pkg === 'services-manager') {
			return url
		}
		if (useLocalPiler) {
			return LOCAL_PILER_STATICS
		}
		return debug ? `${url}/debug` : url
	}

	const builderBundleUrlFor = (pkg: string, format: string) => {
		const suffix = format === 'es' ? 'mjs' : 'js'
		const baseUrl = getUrlFor(pkg)
		return `${baseUrl}/${format}/${pkg}.${suffix}`
	}

	const urls = {
		umd: {},
		esm: {
			'services-manager': `${getUrlFor('services-manager')}/services-manager.js`,
			'services-manager/helpers': `${getUrlFor('services-manager')}/helpers.js`,
			'services-manager/core-services/signals': `${getUrlFor('services-manager')}/core-services/signals/definition.js`,
			'services-manager-react': process.env.browser
				? `${getUrlFor('services-manager-react')}/browser/index.js`
				: `${getUrlFor('services-manager-react')}/index.js`,
		},
	} as BuilderPublicPackagesUrls

	return builderPublicPackages.reduce((acc, pkg) => {
		acc.umd[pkg] = builderBundleUrlFor(pkg, 'umd')
		acc.esm[pkg] = builderBundleUrlFor(pkg, 'es')
		return acc
	}, urls)
}

// Builder design: a provider resolves both by its manifest moduleSpecifier and by its public npm name, package.name/client.path.
export const getContextProviderModuleSpecifiers = (
	contextSpecifier: BuilderCtxProviderData['contextSpecifier'] | undefined,
	moduleConfiguration: BuilderCtxProviderData['moduleConfiguration']
): Array<string> => {
	const packageName = moduleConfiguration?.package?.name
	const clientPath = moduleConfiguration?.client?.path
	const specifiers = [contextSpecifier?.moduleSpecifier, packageName && clientPath && `${packageName}/${clientPath}`]
	return [...new Set(specifiers.filter((specifier): specifier is string => !!specifier))]
}

const getContextTypeBySpecifier = (clientSpecMap: ViewerClientSpecMap) =>
	Object.values(clientSpecMap).reduce<Record<string, string>>((acc, { components }) => {
		components?.forEach((comp) => {
			if (comp.type !== 'EDITOR_CONTEXT_PROVIDER' || !comp.data.type) {
				return
			}
			const { type, resources, moduleConfiguration } = comp.data
			for (const moduleSpecifier of getContextProviderModuleSpecifiers(
				resources?.contextSpecifier,
				moduleConfiguration
			)) {
				acc[moduleSpecifier] = type
			}
		})
		return acc
	}, {})

// Every builder component on the site, by builder type: the components part of getBuilderComponentsData.
export const getBuilderComponentsFromClientSpecMap = (
	clientSpecMap: ViewerClientSpecMap,
	isBuilderComponentModel?: boolean
): Record<string, BuilderCompData> => {
	// If we are not in builder component model (e.g. in studio/classic), we don't want to load builder components data
	if (!isBuilderComponentModel) {
		return {}
	}
	const contextTypeBySpecifier = getContextTypeBySpecifier(clientSpecMap)
	return Object.values(clientSpecMap).reduce(
		(acc, spec: AppSpecData) => {
			const { appDefinitionId, components } = spec
			if (!components) {
				return acc
			}

			components.forEach((comp) => {
				if (comp.type !== 'EDITOR_REACT_COMPONENT') {
					return
				}
				const { componentId, data } = comp
				const { type, resources } = data
				const sdkUrl = resources?.sdk?.url
				const serverUrl = resources?.server?.url || ''
				const editorComponentUrl = resources?.editor?.componentUrl
				const editorCssUrl = resources?.editor?.cssUrl ?? undefined
				const componentUrl = resources?.client?.componentUrl || ''
				const moduleSpecifier = resources?.client?.moduleSpecifier

				const translations = resources?.translations
				const viewerReducedModel = resources?.viewerReducedModel ?? ''
				const viewerRuntimeCss = resources?.viewerRuntimeCss ?? ''
				if (!type) {
					return
				}
				const moduleConfiguration = data.moduleConfiguration
				const contextDependencies = [
					...new Set(
						[
							...(resources?.client?.dependencies?.contextDependencies ?? []),
							...(resources?.editor?.dependencies?.contextDependencies ?? []),
						].map((dependency) => contextTypeBySpecifier[dependency] ?? dependency)
					),
				]

				acc[type] = {
					appDefinitionId,
					componentId,
					componentType: type,
					componentUrl,
					editorComponentUrl,
					cssUrl: resources?.client?.cssUrl || '',
					editorCssUrl,
					viewerReducedModel,
					viewerRuntimeCss,
					...(sdkUrl && {
						sdkUrl,
					}),
					...(serverUrl && {
						serverUrl,
					}),
					...(translations && {
						translations,
					}),
					...(moduleSpecifier && {
						moduleSpecifier,
					}),
					...(moduleConfiguration && {
						moduleConfiguration,
					}),
					...(contextDependencies.length && {
						contextDependencies,
					}),
				}
			})

			return acc
		},
		{} as Record<string, BuilderCompData>
	)
}

// The fields the viewer reads from a page-scoped builder component (resolver, registrar, preset polyfill).
export const toViewerBuilderCompData = ({
	componentId,
	appDefinitionId,
	componentUrl,
	serverUrl,
	moduleSpecifier,
}: BuilderCompData): ViewerBuilderCompData => ({
	componentId,
	appDefinitionId,
	componentUrl,
	...(serverUrl && { serverUrl }),
	...(moduleSpecifier && { moduleSpecifier }),
})

export const getBuilderComponentsData: IGetBuilderComponentsData = (
	clientSpecMap,
	isBuilderComponentModel,
	serviceTopology,
	builderPublicManifest,
	useLocalPiler,
	debug
) => {
	// If we are not in builder component model (e.g. in studio/classic), we don't want to load builder components data
	if (!isBuilderComponentModel) {
		return {
			components: {},
			publicPackagesUrls: {
				esm: {},
				umd: {},
			},
		}
	}
	const componentsData = getBuilderComponentsFromClientSpecMap(clientSpecMap, isBuilderComponentModel)

	return {
		components: componentsData,
		publicPackagesUrls: serviceTopology
			? getPublicPackagesUrls(serviceTopology, builderPublicManifest, useLocalPiler, debug)
			: { esm: {}, umd: {} },
	}
}

export const getBuilderContextProvidersData: IGetBuilderContextProvidersData = (
	clientSpecMap,
	isBuilderComponentModel
) => {
	// If we are not in builder component model (e.g. in studio/classic), we don't want to load builder context providers data
	if (!isBuilderComponentModel) {
		return {}
	}
	const componentsData = Object.values(clientSpecMap).reduce(
		(acc, spec: AppSpecData) => {
			const { appDefinitionId, components } = spec
			if (!components) {
				return acc
			}

			components.forEach((comp) => {
				if (comp.type !== 'EDITOR_CONTEXT_PROVIDER') {
					return
				}
				const { componentId, data } = comp
				const { type, resources, moduleConfiguration } = data
				const contextSpecifier = resources?.contextSpecifier ?? {}
				const dependencies = resources?.dependencies
				const editorComponentUrl = resources?.editor?.url || ''
				const componentUrl = resources?.client?.url || ''
				const deps = (dependencies || {}) as BuilderCtxProviderData['dependencies']
				if (deps.serviceDependencies == null) {
					deps.serviceDependencies = []
				}
				if (deps.contextDependencies == null) {
					deps.contextDependencies = []
				}
				if (!type) {
					return
				}

				acc[type] = {
					appDefinitionId,
					contextSpecifier: contextSpecifier || {},
					componentId,
					dependencies: deps,
					componentUrl,
					editorComponentUrl,
					...(moduleConfiguration && {
						moduleConfiguration,
					}),
				}
			})

			return acc
		},
		{} as Record<string, BuilderCtxProviderData>
	)

	return componentsData
}

export const getBuilderFunctionLibrariesData: IGetBuilderFunctionLibrariesData = (
	clientSpecMap,
	isBuilderComponentModel
) => {
	// If we are not in builder component model (e.g. in studio/classic), we don't want to load builder function libraries data
	if (!isBuilderComponentModel) {
		return {}
	}
	const componentsData = Object.values(clientSpecMap).reduce(
		(acc, spec: AppSpecData) => {
			const { appDefinitionId, components } = spec
			if (!components) {
				return acc
			}

			components.forEach((comp) => {
				if (comp.type !== 'EDITOR_FUNCTION_LIBRARY') {
					return
				}
				const { componentId, data } = comp
				const { type, resources } = data
				const moduleSpecifier = resources?.moduleSpecifier || ''
				const componentUrl = resources?.client?.url || ''
				if (!type) {
					return
				}
				acc[type] = {
					appDefinitionId,
					moduleSpecifier,
					componentId,
					componentUrl,
				}
			})

			return acc
		},
		{} as Record<string, BuilderFuncLibData>
	)

	return componentsData
}

/**
 * Canonical key for a component-exposed ("inline") context, composed from the exposing component's
 * type and the context name. Used as the key in ComponentExposedContextsData and to look up the
 * context's hook/provider in the registry.
 */
export const getComponentExposedContextKey = (componentType: string, contextName: string): string =>
	`${componentType}::${contextName}`

export const getComponentExposedContextsData: IGetComponentExposedContextsData = (
	clientSpecMap,
	isBuilderComponentModel
) => {
	if (!isBuilderComponentModel) {
		return {}
	}
	return Object.values(clientSpecMap).reduce((acc: ComponentExposedContextsData, spec: AppSpecData) => {
		const { appDefinitionId, components } = spec
		if (!components) {
			return acc
		}

		components.forEach((comp) => {
			if (comp.type !== 'EDITOR_REACT_COMPONENT') {
				return
			}
			const { componentId, data } = comp
			const { type, resources } = data
			const contextHooks = resources?.contextHooks
			if (!type || !contextHooks) {
				return
			}
			const componentUrl = resources?.client?.componentUrl || ''
			const editorComponentUrl = resources?.editor?.componentUrl
			const serverUrl = resources?.server?.url

			Object.entries(contextHooks).forEach(([contextName, hook]) => {
				if (!hook) {
					return
				}
				acc[getComponentExposedContextKey(type, contextName)] = {
					appDefinitionId,
					componentId,
					componentType: type,
					contextName,
					componentUrl,
					...(editorComponentUrl && { editorComponentUrl }),
					...(serverUrl && { serverUrl }),
					hook,
				}
			})
		})

		return acc
	}, {})
}

export const getBuilderComponentsTranslations: IGetBuilderComponentsTranslations = (clientSpecMap) => {
	return Object.values(clientSpecMap).reduce(
		(acc, spec: AppSpecData) => {
			const { components } = spec
			if (!components) {
				return acc
			}

			components.forEach((comp) => {
				if (comp.type !== 'EDITOR_REACT_COMPONENT') {
					return
				}
				const { data } = comp
				const { type, resources } = data

				const translations = resources?.translations

				if (type && translations) {
					acc[type] = translations
				}
			})

			return acc
		},
		{} as Record<string, BuilderCompTranslations>
	)
}

const isBuilderData = (type: string) => type?.endsWith('.Data')

export const extractComponentData = (componentData: any): any => {
	if (!componentData || typeof componentData !== 'object') {
		return componentData
	}

	if (Array.isArray(componentData)) {
		return componentData.map((item) => extractComponentData(item))
	}

	if (isBuilderData(componentData.type) && componentData.data) {
		return extractComponentData(componentData.data)
	}

	return Object.entries(componentData).reduce(
		(result, [key, value]) => ({
			...result,
			[key]: extractComponentData(value),
		}),
		{}
	)
}
